import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useVendorMe, useVendorRefresh } from "@/hooks/useVendor";
import { VImageUpload } from "@/components/vendor/VImageUpload";
import Certificate from "@/components/formations/Certificate";
import PhoneField, { isValidPhoneNumber } from "@/components/PhoneField";
import VendorWhatsAppCard from "@/components/vendor/VendorWhatsAppCard";

const inputCls = "h-11 w-full rounded-lg border border-line px-3 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15";

export default function VendorProfile() {
  const { data: vendor } = useVendorMe();
  const refresh = useVendorRefresh();
  const [form, setForm] = useState(null);
  const [cert, setCert] = useState(null);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "" });
  const [saving, setSaving] = useState(false);
  const [savingCert, setSavingCert] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  useEffect(() => {
    if (vendor) {
      setForm({ first_name: vendor.first_name || "", last_name: vendor.last_name || "",
        name: vendor.name || "", phone: vendor.phone || "", country: vendor.country || "" });
      setCert({
        cert_logo: vendor.cert_logo || vendor.shop_logo || "",
        cert_signature: vendor.cert_signature || "",
        cert_stamp: vendor.cert_stamp || "",
        cert_signatory_name: vendor.cert_signatory_name || vendor.name || "",
        cert_signatory_title: vendor.cert_signatory_title || "Formateur",
      });
    }
  }, [vendor]);

  if (!form || !cert) return <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setCertField = (k) => (v) => setCert((c) => ({ ...c, [k]: v }));

  const save = async () => {
    if (form.phone && !isValidPhoneNumber(form.phone)) { toast.error("Numéro de téléphone invalide. Vérifiez l'indicatif et le nombre de chiffres."); return; }
    setSaving(true);
    try { await api.put("/vendor/profile", form); toast.success("Profil mis à jour"); refresh(["vendor-me"]); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const saveCert = async () => {
    setSavingCert(true);
    try { await api.put("/vendor/profile", cert); toast.success("Certificat mis à jour 🎓"); refresh(["vendor-me"]); }
    catch (e) { toast.error(apiError(e)); } finally { setSavingCert(false); }
  };
  const changePwd = async () => {
    setSavingPwd(true);
    try { await api.post("/auth/change-password", pwd); toast.success("Mot de passe modifié"); setPwd({ current_password: "", new_password: "" }); }
    catch (e) { toast.error(apiError(e)); } finally { setSavingPwd(false); }
  };

  const preview = {
    certificate_number: "CERT-ISMO-260901-APERCU",
    user_name: "Nom de l'étudiant",
    formation_title: "Votre formation",
    issued_at: new Date().toISOString(),
    issuer_type: "vendor",
    issuer_name: vendor.shop_name || "Ma boutique",
    issuer_logo: cert.cert_logo,
    issuer_signature: cert.cert_signature,
    issuer_stamp: cert.cert_stamp,
    signatory_name: cert.cert_signatory_name,
    signatory_title: cert.cert_signatory_title,
  };

  return (
    <div className="space-y-6" data-testid="vendor-profile">
      <h1 className="font-display text-2xl font-extrabold text-ink">Profil</h1>

      <div className="rounded-2xl border border-line bg-white p-6">
        <p className="font-display text-base font-bold text-ink">Informations personnelles</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <label className="block"><span className="mb-1.5 block text-[11px] font-bold uppercase text-slate-500">Prénom</span>
            <input value={form.first_name} onChange={set("first_name")} className={inputCls} /></label>
          <label className="block"><span className="mb-1.5 block text-[11px] font-bold uppercase text-slate-500">Nom</span>
            <input value={form.last_name} onChange={set("last_name")} className={inputCls} /></label>
          <PhoneField testId="vprofile" label="Téléphone" value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} />
          <label className="block"><span className="mb-1.5 block text-[11px] font-bold uppercase text-slate-500">Pays</span>
            <input value={form.country} onChange={set("country")} className={inputCls} /></label>
          <label className="block sm:col-span-2"><span className="mb-1.5 block text-[11px] font-bold uppercase text-slate-500">Email</span>
            <input value={vendor.email} disabled className={`${inputCls} bg-slate-50 text-slate-400`} /></label>
        </div>
        <div className="mt-4 flex justify-end">
          <button onClick={save} data-testid="profile-save" disabled={saving} className="rounded-full bg-brand px-8 py-3 text-sm font-bold text-white disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
          </button>
        </div>
      </div>

      <VendorWhatsAppCard vendor={vendor} onUpdated={() => refresh(["vendor-me"])} />

      {/* Certificat de formation — branding du revendeur */}
      <div className="rounded-2xl border border-line bg-white p-6" data-testid="vendor-cert-card">
        <p className="font-display text-base font-bold text-ink">🎓 Mon certificat de formation</p>
        <p className="mt-1 text-sm text-slate-500">
          Personnalisez le certificat remis à VOS étudiants : votre logo apparaît en haut, votre boutique
          comme organisme de formation, et votre signature + cachet en bas. Le sceau ISMO-TECH reste présent
          discrètement comme preuve d'origine.
        </p>
        <div className="mt-5 grid gap-6 lg:grid-cols-2">
          <div className="space-y-4">
            <VImageUpload label="Logo (haut du certificat)" value={cert.cert_logo} onChange={setCertField("cert_logo")} testId="cert-logo" />
            <VImageUpload label="Signature (image)" value={cert.cert_signature} onChange={setCertField("cert_signature")} testId="cert-signature" />
            <VImageUpload label="Cachet / tampon rond" value={cert.cert_stamp} onChange={setCertField("cert_stamp")} testId="cert-stamp" />
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block"><span className="mb-1.5 block text-[11px] font-bold uppercase text-slate-500">Nom du signataire</span>
                <input data-testid="cert-signatory-name" value={cert.cert_signatory_name} onChange={(e) => setCertField("cert_signatory_name")(e.target.value)} className={inputCls} placeholder="Ex : Ismael Konan" /></label>
              <label className="block"><span className="mb-1.5 block text-[11px] font-bold uppercase text-slate-500">Titre</span>
                <input data-testid="cert-signatory-title" value={cert.cert_signatory_title} onChange={(e) => setCertField("cert_signatory_title")(e.target.value)} className={inputCls} placeholder="Ex : Formateur" /></label>
            </div>
            <div className="flex justify-end">
              <button onClick={saveCert} data-testid="cert-save" disabled={savingCert} className="rounded-full bg-brand px-8 py-3 text-sm font-bold text-white disabled:opacity-60">
                {savingCert ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer le certificat"}
              </button>
            </div>
          </div>
          <div>
            <span className="mb-2 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Aperçu en direct</span>
            <div className="overflow-hidden rounded-xl border border-line bg-slate-50 p-3">
              <div style={{ height: 707 * 0.44 }}>
                <div style={{ width: 1000, height: 707, transform: "scale(0.44)", transformOrigin: "top left" }}>
                  <Certificate data={preview} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-line bg-white p-6">
        <p className="font-display text-base font-bold text-ink">Sécurité</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <label className="block"><span className="mb-1.5 block text-[11px] font-bold uppercase text-slate-500">Mot de passe actuel</span>
            <input type="password" value={pwd.current_password} onChange={(e) => setPwd((p) => ({ ...p, current_password: e.target.value }))} className={inputCls} /></label>
          <label className="block"><span className="mb-1.5 block text-[11px] font-bold uppercase text-slate-500">Nouveau mot de passe</span>
            <input type="password" value={pwd.new_password} onChange={(e) => setPwd((p) => ({ ...p, new_password: e.target.value }))} className={inputCls} /></label>
        </div>
        <div className="mt-4 flex justify-end">
          <button onClick={changePwd} data-testid="password-save" disabled={savingPwd || pwd.new_password.length < 6} className="rounded-full border border-line px-6 py-3 text-sm font-bold text-ink hover:border-brand disabled:opacity-50">
            {savingPwd ? <Loader2 className="h-4 w-4 animate-spin" /> : "Changer le mot de passe"}
          </button>
        </div>
      </div>
    </div>
  );
}
